/* Pure layout + triage logic for the home dashboard: no React, no fetching. */
import { EDensity, EVerdict } from "./homeDashboard.types"
import type {
  ICompanyEntry,
  IHomeDashboardLayout,
  IHomeDashboardPrefs,
  IWidgetDefinition,
  IWidgetResult,
  IWidgetRow,
  IWidgetSummary,
  TColumnCount,
} from "./homeDashboard.types"
import { HOME_VIEW_ID } from "./viewRegistry"

export const MAX_COLUMNS = 6
/** Auto layout keeps every column at least this wide */
export const MIN_COLUMN_PX = 500
const DAY_MS = 864e5

export const EMPTY_LAYOUT: IHomeDashboardLayout = { hidden: [], snoozes: {}, placement: {}, order: [] }
export const EMPTY_PREFS: IHomeDashboardPrefs = { ...EMPTY_LAYOUT, views: {} }

export const SNOOZE_OPTIONS: { days: number; label: string }[] = [
  { days: 1, label: "1 day" },
  { days: 3, label: "3 days" },
  { days: 7, label: "1 week" },
  { days: 14, label: "2 weeks" },
  { days: 30, label: "1 month" },
]

const VERDICT_ORDER: Record<EVerdict, number> = {
  [EVerdict.ACT]: 0,
  [EVerdict.WATCH]: 1,
  [EVerdict.UNKNOWN]: 2,
  [EVerdict.CLEAR]: 3,
}

export const worstVerdict = (verdicts: EVerdict[], fallback = EVerdict.CLEAR): EVerdict =>
  verdicts.reduce((worst, v) => (VERDICT_ORDER[v] < VERDICT_ORDER[worst] ? v : worst), fallback)

/** Loud rows by verdict (stable), then quiet rows collapsed at the end. */
export const splitRows = (result: IWidgetResult): { loud: IWidgetRow[]; quiet: IWidgetRow[] } => {
  const rows = result.ordered
    ? result.rows.slice()
    : result.rows.slice().sort((a, b) => VERDICT_ORDER[a.verdict] - VERDICT_ORDER[b.verdict])
  return { loud: rows.filter((row) => !row.quiet), quiet: rows.filter((row) => row.quiet) }
}

/** Rail counts match what the widget shows loud; quiet (folded) rows never count. */
export const summarize = (result: IWidgetResult | undefined, error: boolean): IWidgetSummary => {
  const loud = result?.rows.filter((row) => !row.quiet) ?? []
  return {
    verdict: result?.verdict ?? EVerdict.UNKNOWN,
    act: loud.filter((row) => row.verdict === EVerdict.ACT).length,
    watch: loud.filter((row) => row.verdict === EVerdict.WATCH).length,
    error,
  }
}

export const rowMatches = (row: IWidgetRow, query: string): boolean =>
  !query ||
  `${row.title} ${row.sub ?? ""} ${row.meta ?? ""} ${row.verdict} ${row.pill?.text ?? ""}`.toLowerCase().includes(query)

const clampColumns = (n: number): TColumnCount => Math.max(1, Math.min(MAX_COLUMNS, Math.floor(n))) as TColumnCount

/** override 0 = auto from the available width */
export const columnCountFor = (width: number, override: number): TColumnCount =>
  override > 0 ? clampColumns(override) : clampColumns(width / MIN_COLUMN_PX)

export const defaultColumnFor = (def: IWidgetDefinition, n: TColumnCount): number => {
  for (let k = n; k >= 1; k--) {
    const col = def.columns[k as TColumnCount]
    if (col) return Math.min(col, n)
  }
  return 1
}

export const isSnoozed = (prefs: IHomeDashboardLayout, id: string, now: number): boolean => {
  const until = prefs.snoozes[id]
  return !!until && Date.parse(until) > now
}

export const isShown = (prefs: IHomeDashboardLayout, def: IWidgetDefinition, now: number): boolean =>
  !prefs.hidden.includes(def.id) && !isSnoozed(prefs, def.id, now)

/** Registry order, re-ranked by the saved order (ids missing from it keep their registry slot). */
export const orderedDefs = (defs: IWidgetDefinition[], prefs: IHomeDashboardLayout): IWidgetDefinition[] => {
  const rank = new Map(prefs.order.map((id, i) => [id, i]))
  return defs
    .map((def, i) => ({ def, key: rank.get(def.id) ?? i - defs.length }))
    .sort((a, b) => a.key - b.key)
    .map((x) => x.def)
}

export const columnOf = (def: IWidgetDefinition, prefs: IHomeDashboardLayout, n: TColumnCount): number =>
  Math.min(n, prefs.placement[String(n)]?.[def.id] ?? defaultColumnFor(def, n))

/** Visible widgets per column, left to right. */
export const placeWidgets = (
  defs: IWidgetDefinition[],
  prefs: IHomeDashboardLayout,
  n: TColumnCount,
  now: number,
): IWidgetDefinition[][] => {
  const cols: IWidgetDefinition[][] = Array.from({ length: n }, () => [])
  for (const def of orderedDefs(defs, prefs)) {
    if (isShown(prefs, def, now)) cols[columnOf(def, prefs, n) - 1]?.push(def)
  }
  return cols
}

export const snoozeWidget = (
  prefs: IHomeDashboardLayout,
  id: string,
  days: number,
  now: number,
): IHomeDashboardLayout => {
  const snoozes = { ...prefs.snoozes }
  if (days > 0) snoozes[id] = new Date(now + days * DAY_MS).toISOString()
  else delete snoozes[id]
  return { ...prefs, snoozes }
}

/** Freeze the current order into prefs so a move only shifts what the user touched. */
const withFullOrder = (defs: IWidgetDefinition[], prefs: IHomeDashboardLayout): string[] =>
  orderedDefs(defs, prefs).map((def) => def.id)

export const setWidgetShown = (
  defs: IWidgetDefinition[],
  prefs: IHomeDashboardLayout,
  id: string,
  shown: boolean,
): IHomeDashboardLayout => {
  const hidden = prefs.hidden.filter((x) => x !== id)
  const order = withFullOrder(defs, prefs)
  // Adding a widget back also wakes it, so it shows up right away
  const snoozes = shown ? snoozeWidget(prefs, id, 0, 0).snoozes : prefs.snoozes
  return { ...prefs, hidden: shown ? hidden : [...hidden, id], order, snoozes }
}

export enum EMove {
  LEFT = "left",
  RIGHT = "right",
  UP = "up",
  DOWN = "down",
}

export const moveWidget = (
  defs: IWidgetDefinition[],
  prefs: IHomeDashboardLayout,
  n: TColumnCount,
  id: string,
  move: EMove,
  now: number,
): IHomeDashboardLayout => {
  const def = defs.find((d) => d.id === id)
  if (!def) return prefs
  const order = withFullOrder(defs, prefs)
  const col = columnOf(def, prefs, n)
  if (move === EMove.LEFT || move === EMove.RIGHT) {
    const next = Math.max(1, Math.min(n, col + (move === EMove.LEFT ? -1 : 1)))
    const key = String(n)
    return { ...prefs, order, placement: { ...prefs.placement, [key]: { ...prefs.placement[key], [id]: next } } }
  }
  const siblings = placeWidgets(defs, { ...prefs, order }, n, now)[col - 1] ?? []
  const i = siblings.findIndex((d) => d.id === id)
  const other = siblings[i + (move === EMove.UP ? -1 : 1)]
  if (i < 0 || !other) return { ...prefs, order }
  const a = order.indexOf(id)
  const b = order.indexOf(other.id)
  const swapped = order.slice()
  swapped[a] = other.id
  swapped[b] = id
  return { ...prefs, order: swapped }
}

export const ago = (iso: string | Date | null | undefined, now: number): string => {
  if (!iso) return "never"
  const s = (now - new Date(iso).getTime()) / 1000
  if (s < 0) return "soon"
  if (s < 90) return "now"
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < (DAY_MS / 1000) * 1.5) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / (DAY_MS / 1000))}d ago`
}

const DENSITY_CYCLE: EDensity[] = [EDensity.COMPACT, EDensity.STANDARD, EDensity.RELAXED]

export const DENSITY_OPTIONS: { value: EDensity; label: string }[] = [
  { value: EDensity.COMPACT, label: "Compact" },
  { value: EDensity.STANDARD, label: "Standard" },
  { value: EDensity.RELAXED, label: "Relaxed" },
]

export const nextDensity = (density: EDensity): EDensity =>
  DENSITY_CYCLE[(DENSITY_CYCLE.indexOf(density) + 1) % DENSITY_CYCLE.length] ?? EDensity.STANDARD

/** Device choice, else the widget's own default, else standard. */
export const densityOf = (def: IWidgetDefinition, chosen: Record<string, EDensity>): EDensity =>
  chosen[def.id] ?? def.defaultDensity ?? EDensity.STANDARD

/** j/k movement: nothing selected starts at the first (down) or last (up) item; ends clamp instead of wrapping. */
export const stepIndex = (length: number, current: number, delta: 1 | -1): number => {
  if (length === 0) return -1
  if (current < 0) return delta === 1 ? 0 : length - 1
  return Math.max(0, Math.min(length - 1, current + delta))
}

/** Key presses inside inputs, menus and editable text belong to them, not to page shortcuts. */
export const isTyping = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && !!target.closest("input, textarea, select, [contenteditable], [role=menu]")

/** Shortcuts never fire with a modifier held, so browser and OS chords keep working. */
export const hasModifier = (e: KeyboardEvent): boolean => e.metaKey || e.ctrlKey || e.altKey

/* ─── Views ─────────────────────────────────────────── */
/* Which views exist and what they show is hardcoded in viewRegistry.ts; prefs only hold each one's layout. */

const layoutFields = ({ hidden, snoozes, placement, order }: IHomeDashboardLayout): IHomeDashboardLayout => ({
  hidden,
  snoozes,
  placement,
  order,
})

export const layoutOf = (prefs: IHomeDashboardPrefs, viewId: string): IHomeDashboardLayout =>
  viewId === HOME_VIEW_ID ? layoutFields(prefs) : layoutFields({ ...EMPTY_LAYOUT, ...prefs.views[viewId] })

export const withLayout = (
  prefs: IHomeDashboardPrefs,
  viewId: string,
  layout: IHomeDashboardLayout,
): IHomeDashboardPrefs =>
  viewId === HOME_VIEW_ID
    ? { ...prefs, ...layoutFields(layout) }
    : { ...prefs, views: { ...prefs.views, [viewId]: layoutFields(layout) } }

// ─── Company filter ───────────────────────────────────────
/** Keys that don't share their company's slug or a "slug-" prefix */
const COMPANY_ALIASES: Record<string, string> = { aixinternal: "aix" }

/**
 * Map whatever key a row carries to a company slug: exact slug, a project key from monitor_projects, an alias,
 * the company name, then the longest "slug-" prefix ("vidmax-vite" → vidmax). Unknown keys return null.
 */
export const resolveCompany = (key: string | null | undefined, directory: ICompanyEntry[]): string | null => {
  if (!key) return null
  const k = key.trim().toLowerCase()
  const exact = directory.find(
    (c) => c.slug === k || c.projects.includes(k) || c.name.toLowerCase() === k || COMPANY_ALIASES[k] === c.slug,
  )
  if (exact) return exact.slug
  const prefixed = directory.filter((c) => k.startsWith(`${c.slug}-`)).sort((a, b) => b.slug.length - a.slug.length)[0]
  return prefixed?.slug ?? null
}

/** `?company=all` = explicitly every company, so a view with a default company can still show all */
export const ALL_COMPANIES_PARAM = "all"

/** `?company=vidmax,pebble` ↔ list; empty (or `all`) means no filter. */
export const parseCompanyParam = (value: unknown): string[] =>
  typeof value === "string" && value !== ALL_COMPANIES_PARAM
    ? [
        ...new Set(
          value
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean),
        ),
      ]
    : []

/** A widget scoped to companies shows only when the filter includes one of them; unscoped widgets always show. */
export const widgetInCompanies = (def: IWidgetDefinition, selected: string[]): boolean =>
  !selected.length || !def.companies?.length || def.companies.some((slug) => selected.includes(slug))

/**
 * Rows of a widget that's scoped to the selected companies all count; otherwise a row needs a company in the
 * filter. No filter keeps everything.
 */
export const rowInCompanies = (
  row: IWidgetRow,
  def: IWidgetDefinition,
  selected: string[],
  directory: ICompanyEntry[],
): boolean => {
  if (!selected.length) return true
  if (def.companies?.some((slug) => selected.includes(slug))) return true
  const slug = resolveCompany(row.company, directory)
  return !!slug && selected.includes(slug)
}

/** No `?company=` → the view's default; anything else (incl. `all`) is an explicit choice. */
export const selectedCompaniesFor = (value: unknown, fallback: string[]): string[] =>
  value === undefined ? fallback : parseCompanyParam(value)

/** Clearing the filter in a view with a default writes `all`, otherwise the default would come straight back. */
export const companyParamFor = (slugs: string[], fallback: string[]): string | undefined => {
  if (slugs.length) return slugs.join(",")
  return fallback.length ? ALL_COMPANIES_PARAM : undefined
}
